import { useState } from "react";
import { motion } from "framer-motion";
import { ShoppingCartSimple, Crosshair } from "@phosphor-icons/react";
import { RARITY, ART, nameSizeClass } from "@/lib/format";
import GP from "@/components/GP";
import { useCart } from "@/context/CartContext";
import ProductModal from "@/components/ProductModal";

export default function ProductCard({ p, index = 0 }) {
  const [open, setOpen] = useState(false);
  const { add, has } = useCart();
  const r = RARITY[p.rarity] || RARITY.common;
  const premium = p.rarity === "legendary" || p.rarity === "mythic";
  const isName = p.art === "chrome" && !p.image_url;
  const inCart = has(p.id);

  const body = (
    <div className="relative h-full group" onMouseMove={(e) => {
      const el = e.currentTarget; const b = el.getBoundingClientRect();
      el.style.setProperty("--rx", `${((e.clientY - b.top) / b.height - 0.5) * -6}deg`);
      el.style.setProperty("--ry", `${((e.clientX - b.left) / b.width - 0.5) * 8}deg`);
    }} onMouseLeave={(e) => { e.currentTarget.style.setProperty("--rx", "0deg"); e.currentTarget.style.setProperty("--ry", "0deg"); }}
      style={{ transform: "perspective(900px) rotateX(var(--rx,0)) rotateY(var(--ry,0))", transition: "transform 0.2s ease" }}>
      <div className="relative h-full bg-[#0b0b0b] overflow-hidden crosshair-corner scanlines cursor-pointer"
        style={{ boxShadow: premium ? `0 0 40px -10px ${r.glow}` : "none" }}
        onClick={() => setOpen(true)} data-testid={`product-card-${p.id}`}>
        {!isName && (
          <img src={p.image_url || ART[p.art] || ART.explosion} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-70 group-hover:scale-110 transition-[opacity,transform] duration-500" />
        )}
        {isName && (
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `radial-gradient(circle at 50% 40%, ${r.glow}, transparent 70%)` }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0b] via-[#0b0b0b]/40 to-transparent" />
        <div className="relative z-10 p-5 flex flex-col h-full min-h-[210px]">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold tracking-[0.22em] uppercase px-2 py-1 border" style={{ color: r.color, borderColor: r.color + "66" }}>{r.label}</span>
            {p.stock === 1 && <span className="text-[10px] tracking-[0.2em] uppercase text-[#ff2a55] animate-flicker">1 of 1</span>}
          </div>
          <div className="flex-1 flex items-center py-5">
            <div className={`font-display leading-none break-words ${isName ? `${nameSizeClass(p.title)} chrome-text-sm group-hover:animate-flicker` : "text-3xl text-white"}`}
              style={isName ? { filter: `drop-shadow(0 4px 0 #6a001c) drop-shadow(0 0 18px ${r.glow})` } : undefined}>
              {p.title}
            </div>
          </div>
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#888]">{p.obo ? "Price" : "GP"}</div>
              <div className="font-rs text-3xl leading-none" data-testid={`product-price-${p.id}`}><GP m={p.price} obo={p.obo} coin /></div>
            </div>
            {p.obo ? (
              <button className="btn-ghost !py-2 !px-3 text-sm" onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid={`product-offer-button-${p.id}`}>
                <Crosshair size={18} weight="duotone" /> Offer
              </button>
            ) : (
              <button className="btn-bomb !py-2 !px-3 text-sm" disabled={inCart}
                onClick={(e) => { e.stopPropagation(); add(p); }} data-testid={`product-add-button-${p.id}`}>
                <ShoppingCartSimple size={18} weight="duotone" /> {inCart ? "Locked" : "Lock in"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.45, delay: Math.min(index, 8) * 0.04 }} className="h-full">
        {premium ? <div className="beam-wrap h-full"><div className="beam-inner">{body}</div></div> : <div className="border border-white/10 hover:border-[#ff2a55]/60 transition-colors h-full">{body}</div>}
      </motion.div>
      {open && <ProductModal p={p} onClose={() => setOpen(false)} />}
    </>
  );
}
